/**
 * 全站前端请求防护：限制同时在飞请求、取消重复 GET、超时后短冷却，
 * 避免页面连刷把后端 worker 打满导致全站进不去。
 *
 * 超额时排队等待，不直接拒绝，以免正常功能（如下拉字典）被卡死。
 */

/** 同时允许的最大在飞 API 数（不含被跳过防护的鉴权/字典类） */
export const MAX_INFLIGHT = 6

/** 同一 URL+method+params 超时后冷却毫秒 */
export const TIMEOUT_COOLDOWN_MS = 4000

/** 排队等待名额的最长时间；超时后仍放行，避免功能永久失败 */
export const QUEUE_MAX_WAIT_MS = 45000

const inflight = new Set()
/** key -> AbortController（仅对可合并的 GET） */
const getControllers = new Map()
/** key -> 冷却截止时间戳 */
const cooldownUntil = new Map()
/** 等待名额的队列：{ resolve, timer } */
const waitQueue = []

function normalizeUrl (url = '') {
  return String(url || '')
}

function stableParams (params) {
  if (!params || typeof params !== 'object') return ''
  try {
    const keys = Object.keys(params).sort()
    return keys.map((k) => `${k}=${JSON.stringify(params[k])}`).join('&')
  } catch {
    return ''
  }
}

function sleep (ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function requestGuardKey (config) {
  const method = String(config.method || 'get').toLowerCase()
  const url = normalizeUrl(config.url)
  const params = method === 'get' ? stableParams(config.params) : ''
  return `${method} ${url}?${params}`
}

/** 轻量主数据 / 字典：不占防护名额，保证下拉等控件可用 */
function isLightweightMasterDataUrl (url) {
  return /\/api\/(process-routes|business-statuses|order-item-stages|order-types|brands|seasons|customers\/options|colors|units|warehouses|material-categories|users\/assignable|suppliers|processing-content-types|storage-locations|material-identities|roles|permissions\/catalog)(\/|$|\?)/i.test(
    url
  )
}

export function shouldSkipRequestGuard (config) {
  if (config?.skipRequestGuard) return true
  const url = normalizeUrl(config.url)
  // 鉴权与心跳不占名额、不合并
  if (/\/api\/auth\/(login|logout|verify)\b/.test(url)) return true
  if (isLightweightMasterDataUrl(url)) return true
  return false
}

function pumpWaitQueue () {
  while (waitQueue.length && inflight.size < MAX_INFLIGHT) {
    const entry = waitQueue.shift()
    if (!entry) break
    if (entry.timer) {
      clearTimeout(entry.timer)
      entry.timer = null
    }
    try {
      entry.resolve()
    } catch {
      /* ignore */
    }
  }
}

function waitForInflightSlot () {
  if (inflight.size < MAX_INFLIGHT) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    const entry = { resolve, timer: null }
    entry.timer = setTimeout(() => {
      const idx = waitQueue.indexOf(entry)
      if (idx >= 0) waitQueue.splice(idx, 1)
      // 超时仍放行：宁可短时略超并发，也不阻断业务
      resolve()
    }, QUEUE_MAX_WAIT_MS)
    waitQueue.push(entry)
  })
}

/**
 * 请求发出前：冷却等待、并发排队、GET 去重取消上一笔。
 * 返回 null 表示放行；仅在调用方 abort 等极端情况返回 Error。
 */
export async function beforeRequestGuard (config) {
  if (shouldSkipRequestGuard(config)) return null

  const key = requestGuardKey(config)

  // 冷却：等待结束后再发，而不是直接失败
  for (;;) {
    const cool = cooldownUntil.get(key) || 0
    const waitMs = cool - Date.now()
    if (waitMs <= 0) break
    await sleep(Math.min(waitMs, TIMEOUT_COOLDOWN_MS))
  }

  await waitForInflightSlot()

  const method = String(config.method || 'get').toLowerCase()
  // GET：同一查询未完成时取消上一笔，避免叠请求
  if (method === 'get' && !config.skipGetDedupe) {
    const prev = getControllers.get(key)
    if (prev) {
      try {
        prev.abort()
      } catch {
        /* ignore */
      }
      getControllers.delete(key)
    }
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null
    if (ac) {
      const userSignal = config.signal
      if (userSignal) {
        if (userSignal.aborted) {
          ac.abort()
        } else {
          userSignal.addEventListener('abort', () => ac.abort(), { once: true })
        }
      }
      config.signal = ac.signal
      config._erpGuardAbort = ac
      getControllers.set(key, ac)
    }
  }

  config._erpGuardKey = key
  inflight.add(config)
  return null
}

export function afterRequestGuard (config, error) {
  if (!config || shouldSkipRequestGuard(config)) {
    pumpWaitQueue()
    return
  }

  if (config._erpGuardKey && config._erpGuardAbort) {
    const cur = getControllers.get(config._erpGuardKey)
    if (cur === config._erpGuardAbort) {
      getControllers.delete(config._erpGuardKey)
    }
  }
  inflight.delete(config)

  const aborted =
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError' ||
    /canceled|cancelled|aborted/i.test(String(error?.message || ''))

  const timedOut =
    error &&
    !aborted &&
    (error.code === 'ECONNABORTED' ||
      String(error.message || '').toLowerCase().includes('timeout') ||
      error.response?.status === 504 ||
      error.response?.data?.code === 'db_query_timeout')

  if (timedOut && config._erpGuardKey) {
    cooldownUntil.set(config._erpGuardKey, Date.now() + TIMEOUT_COOLDOWN_MS)
  }

  pumpWaitQueue()
}

export function getInflightCount () {
  return inflight.size
}

export function getRequestGuardQueueLength () {
  return waitQueue.length
}
